import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ShieldCheck, MapPin, Scale, FileCheck, ArrowRight, Globe2, Lock, Clock } from "lucide-react";

const services = [
  { icon: MapPin, title: "Address Verification", desc: "Physical & digital residence confirmation across 100+ countries with local field partners." },
  { icon: Scale, title: "Litigation Checks", desc: "Civil, Criminal or combined court-record searches with jurisdictional accuracy." },
  { icon: FileCheck, title: "ID Checks", desc: "Passport, national ID and government-issued document validation against source registries." },
];

const stats = [
  { k: "100+", v: "Countries served" },
  { k: "48h", v: "Avg. TAT for ID checks" },
  { k: "99.4%", v: "First-time accuracy" },
];

export default function HomePage() {
  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden hero-grid">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 pt-20 pb-24 lg:pt-28 lg:pb-32">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white/70 backdrop-blur px-3 py-1.5 text-xs font-medium text-slate-700">
              <ShieldCheck size={14} className="text-[#1E3A8A]" /> Overseas Background Verification, done right
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-900 mt-6 leading-[1.05]">
              Hire globally.<br />
              <span className="text-[#1E3A8A]">Verify locally.</span>
            </h1>
            <p className="mt-6 text-base sm:text-lg text-slate-600 max-w-2xl leading-relaxed">
              Court, address and identity verification for candidates across international
              jurisdictions.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/register" data-testid="hero-register-btn">
                <Button className="pill-btn bg-[#1E3A8A] hover:bg-[#1E40AF] text-white h-12 px-6">
                  Start verifying <ArrowRight size={16} className="ml-2" />
                </Button>
              </Link>
              <Link to="/services" data-testid="hero-services-btn">
                <Button variant="outline" className="pill-btn h-12 px-6 border-slate-300 text-slate-800 hover:bg-slate-900 hover:text-white hover:border-slate-900">
                  Explore services
                </Button>
              </Link>
            </div>
          </div>

          <div className="mt-16 grid grid-cols-2 md:grid-cols-3 gap-6">
            {stats.map((s) => (
              <div key={s.v} className="border border-slate-200 rounded-2xl bg-white/70 backdrop-blur px-5 py-6">
                <div className="font-display text-3xl font-bold text-slate-900">{s.k}</div>
                <div className="mt-1 text-xs font-mono-data uppercase tracking-widest text-slate-500">{s.v}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-20 bg-white border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="max-w-2xl">
            <div className="font-mono-data text-[11px] uppercase tracking-[0.2em] text-[#1E3A8A]">Services</div>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
              Three checks. Zero blind spots.
            </h2>
            <p className="mt-4 text-slate-600 leading-relaxed">
              We keep our focus narrow so our execution is deep. Every case is jurisdiction-aware,
              audit-logged and delivered with a signed verification report.
            </p>
          </div>
          <div className="mt-12 grid md:grid-cols-3 gap-6">
            {services.map(({ icon: Icon, title, desc }) => (
              <div key={title} className="card-elevate border border-slate-200 rounded-2xl p-7 bg-white">
                <div className="w-11 h-11 rounded-xl bg-[#1E3A8A]/10 text-[#1E3A8A] grid place-items-center"><Icon size={22} /></div>
                <h3 className="mt-5 font-display text-xl font-semibold text-slate-900">{title}</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Value props */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid md:grid-cols-3 gap-8">
          {[
            { icon: Globe2, title: "Overseas reach", desc: "Verified partners in 100+ countries — no delegated middlemen." },
            { icon: Lock, title: "Data handled right", desc: "Encrypted at rest, purpose-limited access, GDPR & DPDPA aligned." },
            { icon: Clock, title: "Fast, honest TATs", desc: "Realistic timelines up front. If a court is slow, you'll know why." },
          ].map(({ icon: Icon, title, desc }) => (
            <div key={title} className="flex gap-4">
              <Icon size={22} className="mt-1 text-[#1E3A8A]" />
              <div>
                <div className="font-display font-semibold text-slate-900">{title}</div>
                <p className="text-sm text-slate-600 mt-1">{desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="pb-24">
        <div className="max-w-6xl mx-auto px-5 lg:px-8">
          <div className="rounded-3xl bg-slate-900 text-white p-10 lg:p-14 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8">
            <div>
              <h3 className="font-display text-2xl sm:text-3xl font-bold">Ready to run your first overseas check?</h3>
              <p className="mt-2 text-slate-300 max-w-xl">Create a client account and submit a case in under two minutes.</p>
            </div>
            <div className="flex gap-3">
              <Link to="/register"><Button className="pill-btn h-12 px-6 bg-white text-slate-900 hover:bg-slate-100">Sign up</Button></Link>
              <Link to="/contact"><Button variant="outline" className="pill-btn h-12 px-6 border-slate-600 text-white hover:bg-white hover:text-slate-900">Talk to sales</Button></Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
